import {
  CalendarHubClient,
  CourseClassCalendarDto,
  CustomCalendarResponse,
  MsOfferingCalendarDto,
  MsPrivateCalendarDto,
  ProgramManagementCalendar,
} from "@clients/calendarHubClient"
import { ProgramDomain } from "@masterschool/course-builder-api"
import {
  CalendarGroupings,
  MsEventProps,
} from "@masterschool/ui-calendar/dist/src/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "@app/hooks"
import { store } from "@app/store"
import {
  selectClassIdsByProgram,
  selectProgramsWithTracks,
  selectProgramCourseInstanceIdentifiers,
} from "@features/program/programSliceSelectors"
import {
  selectCourseInstanceIdsByIdentifiers,
  selectCourseInstancesClasses,
} from "@features/courseInstance/courseInstanceSelectors"
import {
  buildMsCalendarGroupingsAndFiltersForProgramOwner,
  mapToMsCalendarProps,
} from "./calendarMappers"

export type ProgramClassCalendarsState = Record<
  string,
  { isLoading: boolean; calendars: CourseClassCalendarDto[] }
>

// Loads programs' class calendars for the menu + invite picker.
//
// `eagerProgramIds` (the running programs) are fetched reactively as soon as
// their class IDs resolve in Redux — using the exact selector chain the program
// calendar's useGetCourseClassCalendars walks (program -> instances -> classes),
// just batched per program. Because the IDs come from a live useAppSelector and
// not a store.getState() snapshot, the fetch fires the moment fetchAppData's
// parallel slices settle (no gating, no missed-then-claimed-empty races), so
// EN/DE class leaves are present in the groupings — and therefore the invite
// picker, which renders them flat without ever expanding a group.
//
// Any other program (e.g. an expanded past program) still loads on demand via
// loadProgramClasses. Both write the same classCalendarsByProgram state and
// share the `requested` guard, so a program is never fetched twice.
export const useLazyProgramClassCalendars = (
  eagerProgramIds: string[] = [],
) => {
  const [classCalendarsByProgram, setClassCalendarsByProgram] =
    useState<ProgramClassCalendarsState>({})
  const requested = useRef<Set<string>>(new Set())

  // Reactive class-ID resolution for the eager (running) programs. Stable
  // reference once the data is loaded, so the effect below fires once per
  // program rather than looping.
  const classIdsByProgram = useAppSelector((state) =>
    selectClassIdsByProgram(state, eagerProgramIds),
  )
  useEffect(() => {
    const pending = Object.entries(classIdsByProgram).filter(
      ([programId, classIds]) =>
        classIds.length > 0 && !requested.current.has(programId),
    )
    if (pending.length === 0) {
      return
    }
    pending.forEach(([programId]) => requested.current.add(programId))

    // Fetch ALL pending programs' classes in a few chunked requests, not one
    // request per program. With hundreds of running programs, per-program
    // fetches meant hundreds of responses each triggering a menu re-render —
    // the storm that made chevron clicks get lost ("stuck") and search lag.
    // Chunking also keeps each request body under the size the backend rejects
    // with HTTP 413. One state update flips the spinner on, a second lands all
    // the results — so the menu re-renders ~twice instead of ~hundreds of times.
    setClassCalendarsByProgram((prev) => {
      const next = { ...prev }
      pending.forEach(([programId]) => {
        next[programId] = {
          isLoading: true,
          calendars: prev[programId]?.calendars ?? [],
        }
      })
      return next
    })

    const allClassIds = [...new Set(pending.flatMap(([, ids]) => ids))]
    const CHUNK_SIZE = 1000
    const chunks: string[][] = []
    for (let i = 0; i < allClassIds.length; i += CHUNK_SIZE) {
      chunks.push(allClassIds.slice(i, i + CHUNK_SIZE))
    }

    Promise.all(
      chunks.map((chunk) => CalendarHubClient.getCourseClassesCalendar(chunk)),
    )
      .then((results) => {
        const byClassId = new Map(
          results
            .flat()
            .map((c) => [c.courseClassDetails.courseClassId, c] as const),
        )
        setClassCalendarsByProgram((prev) => {
          const next = { ...prev }
          pending.forEach(([programId, classIds]) => {
            next[programId] = {
              isLoading: false,
              calendars: classIds
                .map((id) => byClassId.get(id))
                .filter((c): c is CourseClassCalendarDto => Boolean(c)),
            }
          })
          return next
        })
      })
      .catch(() => {
        // Release the claims so a later run can retry, and clear the spinner.
        pending.forEach(([programId]) => requested.current.delete(programId))
        setClassCalendarsByProgram((prev) => {
          const next = { ...prev }
          pending.forEach(([programId]) => {
            next[programId] = {
              isLoading: false,
              calendars: prev[programId]?.calendars ?? [],
            }
          })
          return next
        })
      })
  }, [classIdsByProgram])

  const loadProgramClasses = useCallback((programId: string) => {
    if (requested.current.has(programId)) {
      return
    }
    requested.current.add(programId)

    const state = store.getState()
    const identifiers = selectProgramCourseInstanceIdentifiers(state, programId)
    const courseInstances = selectCourseInstanceIdsByIdentifiers(
      state,
      identifiers,
    )
    const courseClassIds = selectCourseInstancesClasses(
      state,
      courseInstances.map((ci) => ci.id),
    ).map((cc) => cc.id)

    if (courseClassIds.length === 0) {
      setClassCalendarsByProgram((prev) => ({
        ...prev,
        [programId]: { isLoading: false, calendars: [] },
      }))
      return
    }

    setClassCalendarsByProgram((prev) => ({
      ...prev,
      [programId]: { isLoading: true, calendars: [] },
    }))
    CalendarHubClient.getCourseClassesCalendar(courseClassIds)
      .then((calendars) =>
        setClassCalendarsByProgram((prev) => ({
          ...prev,
          [programId]: { isLoading: false, calendars },
        })),
      )
      .catch(() =>
        setClassCalendarsByProgram((prev) => ({
          ...prev,
          [programId]: { isLoading: false, calendars: [] },
        })),
      )
  }, [])

  return { classCalendarsByProgram, loadProgramClasses }
}

export const useGetCalendarsForProgramOwner = (
  enabled: boolean = true,
  // null = first load: let the backend default to the staff's home school(s).
  // A non-null array is the explicit set of schools the user has toggled on.
  selectedDomains: ProgramDomain[] | null = null,
) => {
  const [isFetching, setIsFetching] = useState(false)
  const [calendars, setCalendars] = useState<ProgramManagementCalendar>({
    programSchoolCalendars: [],
    programStaffCalendars: [],
    studentCalendars: [],
    allMasterschoolStaffCalendars: [],
  })
  const [homeDomains, setHomeDomains] = useState<ProgramDomain[]>([])
  // Skip the refetch that would otherwise fire when the container seeds its
  // explicit selection from homeDomains (same schools we just loaded).
  const lastFetchedKey = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled) {
      return
    }
    const domains = selectedDomains ?? undefined
    const key = domains ? [...domains].sort().join(",") : "__home__"
    if (key === lastFetchedKey.current) {
      return
    }
    lastFetchedKey.current = key
    setIsFetching(true)
    CalendarHubClient.getProgramCalendars(undefined, domains).then((res) => {
      const { homeDomains: home, ...cals } = res
      setCalendars(cals)
      setHomeDomains((home ?? []) as ProgramDomain[])
      setIsFetching(false)
    })
  }, [enabled, selectedDomains])
  return {
    calendars,
    homeDomains,
    isFetching,
  }
}

export type InstructorCalendars = {
  personalCalendar?: MsPrivateCalendarDto
  schoolCalendars: MsOfferingCalendarDto[]
}

export type MentorCalendars = {
  personalCalendar?: MsPrivateCalendarDto
  studentCalendars: MsPrivateCalendarDto[]
  schoolCalendars: MsOfferingCalendarDto[]
}

export type CsaCalendars = {
  personalCalendar?: MsPrivateCalendarDto
  studentCalendars: MsPrivateCalendarDto[]
}

// The mentor's campus-visible scope (personal + mentees + their schools), so
// the LMS sidebar matches campus for the same person. Failures degrade to an
// empty set so a non-mentor or a backend hiccup never breaks the menu.
export const useGetMentorCalendars = (enabled: boolean): MentorCalendars => {
  const [calendars, setCalendars] = useState<MentorCalendars>({
    studentCalendars: [],
    schoolCalendars: [],
  })
  useEffect(() => {
    if (!enabled) {
      return
    }
    CalendarHubClient.getMentorCalendars()
      .then((res) => setCalendars(res))
      .catch(() => setCalendars({ studentCalendars: [], schoolCalendars: [] }))
  }, [enabled])
  return calendars
}

// The career-success advisor's campus-visible scope (personal + students).
export const useGetCsaCalendars = (enabled: boolean): CsaCalendars => {
  const [calendars, setCalendars] = useState<CsaCalendars>({
    studentCalendars: [],
  })
  useEffect(() => {
    if (!enabled) {
      return
    }
    CalendarHubClient.getCsaCalendars()
      .then((res) => setCalendars(res))
      .catch(() => setCalendars({ studentCalendars: [] }))
  }, [enabled])
  return calendars
}

// Fetches the logged-in instructor's own calendars (only when `enabled`, i.e.
// the user has the instructor role). Failures degrade to an empty set so a
// non-instructor or a backend hiccup never breaks the program-owner menu.
export const useGetInstructorCalendars = (
  enabled: boolean,
): InstructorCalendars => {
  const [calendars, setCalendars] = useState<InstructorCalendars>({
    schoolCalendars: [],
  })
  useEffect(() => {
    if (!enabled) {
      return
    }
    CalendarHubClient.getInstructorCalendars()
      .then((res) => setCalendars(res))
      .catch(() => setCalendars({ schoolCalendars: [] }))
  }, [enabled])
  return calendars
}

// Lazy "Past programs" section for admins: fetches past programs' calendars only
// the first time the section is expanded, keeping the default (running) list
// light. Returns undefined when not enabled, so nothing renders.
export const useLazyPastPrograms = (enabled: boolean) => {
  const programs = useAppSelector(selectProgramsWithTracks)
  const [groupings, setGroupings] = useState<CalendarGroupings>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const onFirstExpand = useCallback(() => {
    if (hasLoaded || isLoading) {
      return
    }
    setIsLoading(true)
    CalendarHubClient.getProgramCalendars("past")
      .then((res) => {
        setGroupings(
          buildMsCalendarGroupingsAndFiltersForProgramOwner(res, programs)
            .groupings,
        )
        setHasLoaded(true)
      })
      .catch(() => setGroupings([]))
      .finally(() => setIsLoading(false))
  }, [hasLoaded, isLoading, programs])

  if (!enabled) {
    return undefined
  }

  return {
    title: "Past programs",
    isLoading,
    onFirstExpand,
    groupings,
  }
}

export const useGetCalendarReferencedByEvents = (msEvents: MsEventProps[]) => {
  const participantCalendarIds = useMemo(
    () => msEvents.flatMap((e) => e.participantCalendarIds || []).msUnique(),
    [msEvents],
  )
  const [calendars, setCalendars] = useState<CustomCalendarResponse>({
    personalCalendars: [],
    schoolCalendars: [],
  })
  useEffect(() => {
    CalendarHubClient.getCalendarsById(participantCalendarIds).then((res) => {
      setCalendars(res)
    })
  }, [participantCalendarIds])

  const fullCalendarList = calendars
    ? [...calendars.personalCalendars, ...calendars.schoolCalendars]
    : []
  return fullCalendarList
}

export const useGetCalendarById = (
  msEvents: MsEventProps[],
  calendars: (
    | MsPrivateCalendarDto
    | MsOfferingCalendarDto
    | CourseClassCalendarDto
  )[],
) => {
  const calendarsReferencedByEvents = useGetCalendarReferencedByEvents(msEvents)
  const getCalendarById = useCallback(
    (calendarId: string) => {
      const calendar =
        calendars.find((c) => c.id === calendarId) ||
        calendarsReferencedByEvents.find((c) => c.id === calendarId)
      return calendar ? mapToMsCalendarProps(calendar) : undefined
    },
    [calendarsReferencedByEvents, calendars],
  )

  return getCalendarById
}
